// react
import { useContext, useMemo, useState } from 'react';

// hooks
import { useFetch } from '../../../../hooks/useFetch';

// types
import {
	Contract,
	Entity,
	Frame,
	Ifr,
	Loan,
	Pagination,
	Transaction,
	TransactionAugmented,
} from '../../../../@types';
import { transactionStatuses } from '../../../../@types/unions';

// router
import { useLocation, useSearchParams } from 'react-router-dom';

// i18next
import { useTranslation } from 'react-i18next';

// context
import {
	ContextProject,
	ContextUserProjectPermissions,
} from '../../../pages/Project/Project.context';

// mui
import { Grid } from '@mui/material';

// ui
import { BtnExport } from '../../../ui/BtnExport/BtnExport';
import { BtnFilters } from '../../../ui/BtnFilters/BtnFilters';
import { BtnLegendTransaction } from '../../../ui/BtnLegendTransaction/BtnLegendTransaction';
import { EmptyProps } from '../../../ui/Empty/Empty';
import { FilterChangeEvent, FilterProps } from '../../../ui/Filters/Filters';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';

// cards
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';
import { CardTransaction } from '../../../cards/CardTransaction/CardTransaction';

// fields
import { SelectTransactionPage } from '../../../fields/SelectTransactionPage/SelectTransactionPage';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// utils
import { translateStatus } from '../../../../utils/translations';

// config
import { featureFlags } from '../../../../config/featureFlags';

// props
export interface TablePaymentsProps {
	frame?: Frame;
	hasExport?: boolean;
	hasLegend?: boolean;
	hasSearch?: boolean;
	hasSelectPage?: boolean;
	isLoading?: boolean;
	pagination: Pagination;
	refetchTransactions?: () => void;
	transactions: Transaction[];
}

export const TablePayments = ({
	frame = 'PROJECT',
	hasExport = true,
	hasLegend = true,
	hasSearch = true,
	hasSelectPage = true,
	isLoading,
	pagination,
	refetchTransactions,
	transactions,
}: TablePaymentsProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// context
	const { project } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// hooks
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramCategory = searchParams.get('category');
	const paramComponent = searchParams.get('component');
	const paramContract = searchParams.get('contract');
	const paramDialog = searchParams.get('dialog');
	const paramExpenseType = searchParams.get('expenseType');
	const paramLoan = searchParams.get('loan');
	const paramTags = searchParams.get('tags');
	const paramToEntity = searchParams.get('toEntity');
	const paramStatus = searchParams.get('status');
	const paramType = searchParams.get('type');

	// default
	const defaultFiltersValues = {
		category: 'all',
		component: 'all',
		contract: 'all',
		expenseType: 'all',
		loan: 'all',
		status: 'all',
		tags: [],
		toEntity: 'all',
		type: featureFlags['DIRECT'] ? ['DIRECT', 'PAYMENT'] : ['PAYMENT'],
	};

	// state
	const [contracts, setContracts] = useState<Contract[]>([]);
	const [filtersValues, setFiltersValues] = useState({
		category: paramCategory || defaultFiltersValues.category,
		component: paramComponent || defaultFiltersValues.component,
		contract: paramContract || defaultFiltersValues.contract,
		expenseType: paramExpenseType || defaultFiltersValues.expenseType,
		loan: paramLoan || defaultFiltersValues.loan,
		status: paramStatus || defaultFiltersValues.status,
		tags: paramTags ? paramTags.split(',') : defaultFiltersValues.tags,
		toEntity: paramToEntity || defaultFiltersValues.toEntity,
		type: paramType ? paramType.split(',') : defaultFiltersValues.type,
	});
	const [ifrs, setIfrs] = useState<Ifr[]>([]);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [suppliers, setSuppliers] = useState<Entity[]>([]);

	// fetch contracts
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/contracts?fields=id,num&project=${project?.id}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			setContracts(res.data);
		},
	});

	// fetch suppliers
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/entities?fields=id,name&project=${project?.id}&type=SUPPLIER`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			setSuppliers(res.data);
		},
	});

	// fetch loans
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/loans?fields=id,code&project=${project?.id}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			setLoans(res.data);
		},
	});

	// fetch ifrs
	useFetch({
		isEnabled: Boolean(project?.id && featureFlags['IFRS']),
		url: `${process.env.REACT_APP_API_URL}/ifrs/reports?project=${project?.id}&status=!DELETED&fields=currency,dateEnd,dateStart,id,name`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			setIfrs(res.data);
		},
	});

	// vars
	const headers: TableHeaderProps[] = [
		{
			GridProps: {
				lg: 1.5,
			},
			isSortable: true,
			label: t('date'),
			value: 'date',
		},
		{
			GridProps: {
				lg: 2,
			},
			label: t('classification'),
			value: 'expenseType',
		},
	];
	if (frame === 'SUPPLIER') {
		headers.push({
			GridProps: {
				lg: 1,
			},
			label: t('contract'),
			value: 'contact',
		});
	}
	if (frame === 'PROJECT') {
		headers.push({
			GridProps: {
				lg: 1.5,
			},
			label: t('supplier'),
			value: 'toEntity',
		});
	}

	if (frame === 'BENEFICIARY' || frame === 'SUPPLIER') {
		headers.push({
			GridProps: {
				lg: 1.5,
			},
			label: t('entity'),
			value: 'fromEntity',
		});
	}

	headers.push(
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('traceability'),
			value: 'traceability',
		},
		{
			GridProps: {
				lg: 1,
			},
			label: t('amount'),
			value: 'amount',
		}
	);

	if (frame === 'PROJECT') {
		headers.push({
			GridProps: {
				lg: 1,
			},
			label: t('account'),
			value: 'account',
		});
	}

	headers.push({
		GridProps: {
			lg: 1,
		},
		label: t('updated'),
		value: 'modified',
	});

	const idFormFilters = 'form-payments-filters';

	// options
	const optionsContracts = contracts.map((contract) => {
		return {
			label: contract.num,
			value: String(contract.id),
		};
	});

	const optionsSuppliers = suppliers.map((supplier) => {
		return {
			label: supplier.name,
			value: String(supplier.id),
		};
	});

	const optionsStatus = transactionStatuses.map((status) => {
		return {
			label: translateStatus(status, t),
			value: status,
		};
	});

	const optionsLoans = loans.map((loan) => {
		return {
			label: loan.code,
			value: String(loan.id),
		};
	});

	const optionsTypes = [
		{
			label: t('directPayment'),
			value: 'DIRECT',
		},
		{
			label: t('payment'),
			value: 'PAYMENT',
		},
	];

	// handlers
	const handleChangeFilterValue = (e: FilterChangeEvent) => {
		const { name, value } = e.target as HTMLInputElement;
		setFiltersValues({
			...filtersValues,
			[name]: value,
		});
	};

	// filters
	const filters: FilterProps[] = [];

	if (featureFlags['DIRECT']) {
		filters.push({
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-type`,
			label: t('type'),
			multiple: true,
			name: 'type',
			onChange: handleChangeFilterValue,
			options: optionsTypes,
			type: 'SELECT',
			value: filtersValues.type,
		});
	}

	if (frame === 'PROJECT') {
		if (userProjectPermissions?.supplier?.read) {
			filters.push({
				hasAllOption: true,
				hasNoneOption: false,
				hasSearchParam: true,
				id: `${idFormFilters}-supplier`,
				label: t('supplier'),
				name: 'toEntity',
				onChange: handleChangeFilterValue,
				options: optionsSuppliers,
				type: 'SELECT',
				value: filtersValues.toEntity,
			});
		}
	}

	if (frame === 'PROJECT' || frame === 'SUPPLIER') {
		if (userProjectPermissions?.contract?.read) {
			filters.push({
				hasAllOption: true,
				hasNoneOption: false,
				hasSearchParam: true,
				id: `${idFormFilters}-contract`,
				label: t('contract'),
				name: 'contract',
				onChange: handleChangeFilterValue,
				options: optionsContracts,
				type: 'SELECT',
				value: filtersValues.contract,
			});
		}
	}

	if (userProjectPermissions?.component?.read) {
		filters.push({
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-component`,
			label: t('component'),
			name: 'component',
			onChange: handleChangeFilterValue,
			type: 'COMPONENT',
			value: filtersValues.component,
		});
	}

	if (userProjectPermissions?.category?.read) {
		filters.push({
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-category`,
			label: t('category'),
			name: 'category',
			onChange: handleChangeFilterValue,
			type: 'CATEGORY',
			value: filtersValues.category,
		});
	}

	if (userProjectPermissions?.expenseType?.read) {
		filters.push({
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-expense-type`,
			label: t('expenseType'),
			name: 'expenseType',
			onChange: handleChangeFilterValue,
			type: 'EXPENSE_TYPE',
			value: filtersValues.expenseType,
		});
	}

	filters.push({
		hasAllOption: true,
		hasNoneOption: false,
		hasSearchParam: true,
		id: `${idFormFilters}-status`,
		label: t('status'),
		name: 'status',
		onChange: handleChangeFilterValue,
		options: optionsStatus,
		type: 'SELECT',
		value: filtersValues.status,
	});

	if (userProjectPermissions?.loan?.read) {
		filters.push({
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-loan`,
			label: t('financingSource'),
			name: 'loan',
			onChange: handleChangeFilterValue,
			options: optionsLoans,
			type: 'SELECT',
			value: filtersValues.loan,
		});
	}

	if (userProjectPermissions?.tag?.read) {
		filters.push({
			hasSearchParam: true,
			id: `${idFormFilters}-tags`,
			label: t('tags'),
			multiple: true,
			name: 'tags',
			onChange: handleChangeFilterValue,
			type: 'TAGS',
			value: filtersValues.tags,
		});
	}

	const empty = useMemo(() => {
		const emptyProps: EmptyProps = {
			actions: [],
			content: {
				children: t('empty.payments.content'),
			},
			IconCircleProps: {
				color: 'grey',
				type: 'payment',
			},
			title: { children: t('empty.payments.title') },
		};

		if (
			emptyProps.actions &&
			userProjectPermissions?.payment.create &&
			!location.pathname.includes('entity') &&
			project?.version !== '2'
		) {
			emptyProps.actions.push({
				children: t('recordPayment'),
				onClick: () => {
					searchParams.set('dialog', 'recordPayment');
					setSearchParams(searchParams);
				},
				variant: 'contained',
			});
		}

		// redefine empty content if search params exist
		if ([...searchParams].length && !paramDialog) {
			emptyProps.content = {
				children: t('empty.default.content'),
			};
			emptyProps.IconCircleProps = {
				icon: null,
			};
		}

		return emptyProps;
	}, [
		location,
		paramDialog,
		project?.version,
		searchParams,
		setSearchParams,
		t,
		userProjectPermissions,
	]);

	const content = useMemo(() => {
		if (isLoading || !transactions.length) {
			return <CardEmpty empty={empty} isLoading={isLoading} />;
		}

		return (
			<Grid container spacing={2}>
				{transactions.map((transaction, i) => {
					// add ifrs ids to transaction
					const transactionAugmented: TransactionAugmented = {
						...transaction,
						ifrs: ifrs.filter(
							(ifr) => ifr.dateStart <= transaction.date && ifr.dateEnd >= transaction.date
						),
					};

					return (
						<Grid item key={`payment-${i}`} xs={12}>
							<CardTransaction
								frame={frame}
								refetchTransactions={refetchTransactions}
								transaction={transactionAugmented}
							/>
						</Grid>
					);
				})}
			</Grid>
		);
	}, [empty, frame, ifrs, isLoading, refetchTransactions, transactions]);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Grid container spacing={2}>
					{hasSelectPage && (
						<Grid item xs={12} md={3} lg={2}>
							<SelectTransactionPage value="payments" />
						</Grid>
					)}
					{hasSearch && (
						<Grid item xs={12} md={true}>
							<FormSearch InputProps={{ placeholder: t('searchByIdOrEntity') }} />
						</Grid>
					)}
					{hasLegend && (
						<Grid item xs={hasExport ? 4 : 6} md="auto">
							<BtnLegendTransaction />
						</Grid>
					)}
					{hasExport && (
						<Grid item xs={hasLegend ? 4 : 6} md="auto">
							<BtnExport resource="PAYMENT" />
						</Grid>
					)}
					<Grid item container xs={hasExport && hasLegend ? 4 : 6} md="auto">
						<BtnFilters
							filters={filters}
							onReset={() => {
								setFiltersValues(defaultFiltersValues);
							}}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			<Grid item xs={12}>
				<TableFooter numShowing={transactions.length} pagination={pagination} />
			</Grid>
		</Grid>
	);
};
